import axios from 'axios';
import { clearAuthSession, getAuthToken, hasValidAppSession, persistAuthSession, persistRefreshSession } from '../utils/authSession';
import { extractAccessToken, extractRefreshToken, normalizeAccessToken, shouldRefreshAccessToken, toBearerToken } from '../utils/authToken';
import { AUTH_URL, AUTH_URLLive, REFRESH_URL } from "../settings";

export const MAX_AUTH_RETRY_ATTEMPTS = 3;

const DEFAULT_UNAUTHENTICATED_URLS = [
  '/auth',
  '/login',
  '/refresh',
  'https://widget.cloudinary.com',
];

export const configureAuthInterceptors = (axiosInstance, options = {}) => {
  const {
    baseURL = '',
    onAuthFailure = () => { },
    unauthenticatedUrls = DEFAULT_UNAUTHENTICATED_URLS,
  } = options;
  const refreshBaseURL = AUTH_URL || baseURL.replace(/\/api\/?$/, '/');

  let isRefreshing = false;
  let failedQueue = [];
  let refreshPromise = null;

  const shouldSkipAuth = (url = '') =>
    unauthenticatedUrls.some(unAuthenticatedUrl => url?.includes(unAuthenticatedUrl));

  const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
        return;
      }

      resolve(token);
    });
    failedQueue = [];
  };

  const failAuthentication = error => {
    clearAuthSession();
    onAuthFailure(error);
    return Promise.reject(error);
  };

  const clearAuthorizationHeader = requestConfig => {
    if (!requestConfig.headers) {
      return;
    }

    delete requestConfig.headers.Authorization;
  };

  const applyAuthorizationHeader = (requestConfig, token) => {
    const bearerToken = toBearerToken(token);

    if (!bearerToken) {
      clearAuthorizationHeader(requestConfig);
      return false;
    }

    requestConfig.headers = requestConfig.headers || {};
    requestConfig.headers.Authorization = bearerToken;
    return true;
  };

  const retryOriginalRequest = (requestConfig, token) => {
    const nextRetryCount = (requestConfig._authRetryCount || 0) + 1;

    if (nextRetryCount > MAX_AUTH_RETRY_ATTEMPTS) {
      const retryLimitError = new Error('Authentication retry limit exceeded');
      retryLimitError.code = 'AUTH_RETRY_LIMIT_EXCEEDED';
      return failAuthentication(retryLimitError);
    }

    requestConfig._authRetryCount = nextRetryCount;
    if (!applyAuthorizationHeader(requestConfig, token)) {
      const invalidTokenError = new Error('Invalid access token returned during retry');
      invalidTokenError.code = 'INVALID_ACCESS_TOKEN';
      return failAuthentication(invalidTokenError);
    }

    return axiosInstance(requestConfig);
  };

  const refreshAccessToken = () => {
    if (!refreshPromise) {
      isRefreshing = true;
      refreshPromise = axios
        .post('/refresh', null, {
          baseURL: refreshBaseURL,
          withCredentials: true,
        })
        .then(({ data }) => {
          const newToken = extractAccessToken(data);
          const refreshToken = extractRefreshToken(data);

          if (!newToken) {
            const invalidRefreshTokenError = new Error('No valid access token returned from refresh');
            invalidRefreshTokenError.code = 'INVALID_REFRESH_ACCESS_TOKEN';
            throw invalidRefreshTokenError;
          }

          persistAuthSession(newToken);
          persistRefreshSession(refreshToken);
          processQueue(null, newToken);
          return newToken;
        })
        .catch(refreshError => {
          processQueue(refreshError);
          throw refreshError;
        })
        .finally(() => {
          isRefreshing = false;
          refreshPromise = null;
        });
    }

    return refreshPromise;
  };

  axiosInstance.interceptors.request.use(
    async config => {
      // Append timezone to every request if available
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) {
          config.headers = config.headers || {};
          config.headers['X-Timezone'] = tz;
        }
      } catch (e) {
        // ignore timezone extraction errors
      }

      if (shouldSkipAuth(config.url)) {
        return config;
      }

      if (isRefreshing) {
        try {
          const refreshedToken = await refreshPromise;
          applyAuthorizationHeader(config, refreshedToken);
          return config;
        } catch (error) {
          clearAuthorizationHeader(config);
          return failAuthentication(error);
        }
      }

      const token = hasValidAppSession() ? normalizeAccessToken(getAuthToken()) : null;
      if (token && shouldRefreshAccessToken(token)) {
        try {
          const refreshedToken = await refreshAccessToken();
          applyAuthorizationHeader(config, refreshedToken);
          return config;
        } catch (error) {
          clearAuthorizationHeader(config);
          return failAuthentication(error);
        }
      }

      if (!applyAuthorizationHeader(config, token)) {
        clearAuthorizationHeader(config);
      }

      return config;
    },
    error => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      const originalRequest = error.config || {};

      if (error?.response?.status !== 401) {
        return Promise.reject(error);
      }

      // If the request was intended to be unauthenticated (e.g. external APIs or login)
      // and it returns 401, simply reject it without nuking the MillionSpaces session.
      if (shouldSkipAuth(originalRequest.url)) {
        return Promise.reject(error);
      }

      if (originalRequest.url?.includes('/auth/refresh')) {
        return failAuthentication(error);
      }

      if ((originalRequest._authRetryCount || 0) >= MAX_AUTH_RETRY_ATTEMPTS) {
        return failAuthentication(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: token => resolve(retryOriginalRequest(originalRequest, token)),
            reject,
          });
        });
      }

      return refreshAccessToken()
        .then(newToken => retryOriginalRequest(originalRequest, newToken))
        .catch(refreshError => {
          return failAuthentication(refreshError);
        });
    }
  );

  return axiosInstance;
};
